import { Check } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { Pocketbook } from '@/components/pocketbook';
import { useCarriers } from '@/hooks/useCarriers';
import {
  useListDivisionHandlesQuery,
  useListDivisionsQuery,
} from '@/services/api/carriersApiService';

import { CarrierListItem } from './CarrierListItem';
import { HandleListPanel } from './HandleListPanel';
import type { Carrier, ContactHandle } from './types';

export interface HandleSelection {
  carrier: Carrier;
  handle: ContactHandle;
}

interface CarrierPocketbookProps {
  trigger: React.ReactNode;
  onSelect?: (selection: HandleSelection) => void;
  onSelectDivision?: (carrier: Carrier, divisionId: string | null) => void;
  actions?: ('copy' | 'use' | 'link_defaults')[];
  onAttachSelected?: (carrier: Carrier, selected: ContactHandle[]) => void;
  defaultSelectedCarrierId?: string;
}

export const CarrierPocketbook: React.FC<CarrierPocketbookProps> = ({
  trigger,
  onSelect,
  onSelectDivision,
  actions = ['copy', 'use'],
  onAttachSelected,
  defaultSelectedCarrierId,
}) => {
  const { carriers, loading } = useCarriers();
  const [selectedDivisionId, setSelectedDivisionId] = useState<string>('');
  const [hoveredDivisionId, setHoveredDivisionId] = useState<string>('');
  const [activeCarrierId, setActiveCarrierId] = useState<string>('');

  const filterCarriers = (items: Carrier[], query: string): Carrier[] => {
    const lowerQuery = query.toLowerCase();
    return items.filter(
      (carrier) =>
        carrier.name.toLowerCase().includes(lowerQuery) ||
        carrier.aliases?.some((alias) =>
          alias.toLowerCase().includes(lowerQuery),
        ),
    );
  };

  return (
    <Pocketbook<Carrier, ContactHandle>
      trigger={trigger}
      items={carriers}
      loading={loading}
      searchPlaceholder="Search carriers..."
      onSearch={filterCarriers}
      defaultSelectedId={defaultSelectedCarrierId}
      renderLeftItem={(carrier, isSelected) => (
        <CarrierListItem carrier={carrier} isSelected={isSelected} />
      )}
      renderRightPanel={(carrier, handleSelect) => {
        if (!carrier) return null;

        const isActiveCarrier = carrier.id === activeCarrierId;
        const currentSelectedId = isActiveCarrier ? selectedDivisionId : '';
        const currentHoveredId = isActiveCarrier ? hoveredDivisionId : '';
        const divisionToShow = currentHoveredId || currentSelectedId;

        return (
          <div key={carrier.id} className="space-y-2">
            <DivisionSelectorBar
              carrierId={carrier.id}
              selectedDivisionId={currentSelectedId}
              hoveredDivisionId={currentHoveredId}
              onChange={(divisionId) => {
                setActiveCarrierId(carrier.id);
                setSelectedDivisionId(divisionId);
              }}
              onHover={(divisionId) => {
                // Only update hover state if we're viewing the active carrier
                if (isActiveCarrier) {
                  setHoveredDivisionId(divisionId || '');
                }
              }}
              onUse={
                onSelectDivision
                  ? (divisionId) => onSelectDivision(carrier, divisionId)
                  : undefined
              }
            />
            {divisionToShow ? (
              <DivisionHandlePreview
                carrierId={carrier.id}
                divisionId={divisionToShow}
                onSelect={handleSelect}
                actions={actions?.filter((a): a is 'copy' | 'use' => a !== 'link_defaults')}
              />
            ) : (
              <HandleListPanel
                carrier={carrier}
                onSelect={handleSelect}
                actions={actions}
                enableSelection
                onAttachSelected={(c, handles) => {
                  if (onAttachSelected) onAttachSelected(c, handles);
                  handleSelect(
                    (handles && handles[0]) || (c.handles && c.handles[0]),
                  );
                }}
              />
            )}
          </div>
        );
      }}
      onSelect={(carrier, handle) => onSelect && onSelect({ carrier, handle })}
    />
  );
};

interface DivisionSelectorBarProps {
  carrierId: string;
  selectedDivisionId: string;
  hoveredDivisionId?: string;
  onChange: (divisionId: string) => void;
  onHover?: (divisionId: string | null) => void;
  onUse?: (divisionId: string | null) => void;
}

const DivisionSelectorBar: React.FC<DivisionSelectorBarProps> = ({
  carrierId,
  selectedDivisionId,
  hoveredDivisionId,
  onChange,
  onHover,
  onUse,
}) => {
  const { data: divisions = [] } = useListDivisionsQuery(
    { carrierId },
    { skip: !carrierId },
  );
  const defaultDivisionId = useMemo(() => {
    const def = divisions.find((d) => d.isDefault);
    return def ? def.id : '';
  }, [divisions]);

  useEffect(() => {
    if (!selectedDivisionId && defaultDivisionId) {
      onChange(defaultDivisionId);
    }
  }, [defaultDivisionId, onChange, selectedDivisionId]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">Divisions</span>
        {typeof onUse === 'function' && selectedDivisionId && (
          <button
            type="button"
            className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            onClick={() => onUse(selectedDivisionId || null)}
          >
            Use Division
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {divisions.map((d) => {
          const isSelected = d.id === selectedDivisionId;
          const isHovered = d.id === hoveredDivisionId;
          const isPreviewing = isHovered && !isSelected;

          let buttonClasses =
            'px-3 py-1.5 text-xs rounded-md transition-all border-2';
          if (isSelected) {
            buttonClasses +=
              ' bg-blue-600 text-white border-blue-600 font-medium shadow-sm';
          } else if (isPreviewing) {
            buttonClasses +=
              ' bg-blue-50 text-blue-700 border-dashed border-blue-300';
          } else {
            buttonClasses +=
              ' bg-gray-100 text-gray-700 border-transparent hover:bg-gray-200';
          }

          return (
            <button
              key={d.id}
              type="button"
              onClick={() => onChange(d.id)}
              onMouseEnter={() => onHover?.(d.id)}
              onMouseLeave={() => onHover?.(null)}
              className={buttonClasses}
              aria-label={`${d.divisionName}${isSelected ? ' (selected)' : ''}${isPreviewing ? ' (previewing)' : ''}`}
            >
              {d.divisionName}
              {isSelected && <span className="ml-1">✓</span>}
              {isPreviewing && <span className="ml-1">👁</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DivisionHandlePreview: React.FC<{
  carrierId: string;
  divisionId: string;
  onSelect?: (handle: ContactHandle) => void;
  actions?: ('copy' | 'use')[];
}> = ({ carrierId, divisionId, onSelect, actions = ['copy', 'use'] }) => {
  const { data: handles = [] } = useListDivisionHandlesQuery(
    { carrierId, divisionId },
    { skip: !carrierId || !divisionId },
  );
  return (
    <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
      {handles.length === 0 ? (
        <div className="text-sm text-gray-500">
          No handles linked to this division.
        </div>
      ) : (
        handles.map((h) => {
          const value =
            h.value ||
            [h.addressLine1, h.city, h.state, h.zip].filter(Boolean).join(', ');
          return (
            <div
              key={h.id}
              className="flex items-start gap-3 p-2 rounded border border-gray-200"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">
                  {h.name}
                </div>
                <div className="text-sm text-gray-700 break-words">{value}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {h.handleType}
                </div>
              </div>
              {actions.includes('use') && onSelect && (
                <button
                  type="button"
                  onClick={() => onSelect(h)}
                  className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors font-medium flex-shrink-0"
                  title="Use this handle"
                  aria-label={`Use ${h.name}`}
                >
                  <Check className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};
